import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Flame, AlarmClock, Bell, CloudRain, CheckCircle, AlertTriangle, Shield, Eye, Droplets } from 'lucide-react';

const FireFightingSystemPage = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const sections = [
    {
      title: 'Fire Detection Systems',
      description: 'Advanced multi-sensor fire detection technology including photoelectric smoke detectors, thermal imaging cameras, and flame detection systems. Our intelligent detection networks provide early warning capabilities with minimal false alarms for critical infrastructure protection.',
      icon: Eye,
      image: 'https://images.unsplash.com/photo-1595306394931-b35768661692',
    },
    {
      title: 'Fire Suppression Systems',
      description: 'Comprehensive suppression solutions including wet and dry sprinkler systems, clean agent suppression, and specialized foam systems. Our automated suppression networks are designed for rapid response and minimal collateral damage in sensitive environments.',
      icon: CloudRain,
      image: 'https://images.unsplash.com/photo-1690973692388-239878450c7b',
    },
    {
      title: 'Fire Extinguishers & Equipment',
      description: 'Professional-grade portable and wheeled fire extinguishers including ABC dry chemical, CO2, clean agent, and specialized metal fire extinguishers. Strategic placement and maintenance programs ensure immediate response capability for all fire classifications.',
      icon: Flame,
      image: 'https://images.unsplash.com/photo-1722227089176-a981d2544b5f',
    },
    {
      title: 'Fire Alarm & Notification Systems',
      description: 'Integrated fire alarm systems with voice evacuation, mass notification, and emergency communication capabilities. Our networked alarm systems provide zone-specific alerts, automated emergency services notification, and integration with building management systems.',
      icon: Bell,
      image: 'https://images.pexels.com/photos/134065/pexels-photo-134065.jpeg',
    },
    {
      title: 'Types of Fire Fighting Systems',
      description: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Wet Pipe Systems</h4>
              <p className="text-sm text-gray-300">Water-filled pipes with immediate discharge capability</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Dry Pipe Systems</h4>
              <p className="text-sm text-gray-300">Air-pressurized systems for freeze-prone environments</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Deluge Systems</h4>
              <p className="text-sm text-gray-300">High-hazard area protection with simultaneous discharge</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Clean Agent Systems</h4>
              <p className="text-sm text-gray-300">Gaseous suppression for sensitive electronics and data centers</p>
            </div>
          </div>
        </div>
      ),
      icon: Droplets,
      image: 'https://images.pexels.com/photos/189474/pexels-photo-189474.jpeg',
    },
    {
      title: 'System Operation & Control',
      description: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Detection Phase</h4>
              <p className="text-sm text-gray-300">Multi-sensor analysis and threat verification</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Alarm & Notification</h4>
              <p className="text-sm text-gray-300">Immediate occupant and emergency service alerts</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Suppression Activation</h4>
              <p className="text-sm text-gray-300">Automated or manual suppression system deployment</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="font-semibold text-royal-gold mb-2">Monitoring & Control</h4>
              <p className="text-sm text-gray-300">Continuous system status and performance monitoring</p>
            </div>
          </div>
        </div>
      ),
      icon: AlarmClock,
      image: 'https://images.pexels.com/photos/2282658/pexels-photo-2282658.jpeg',
    },
    {
      title: 'Emergency Response Services',
      description: 'Professional emergency response coordination, evacuation planning, and incident management. Our certified emergency response teams provide 24/7 support, rapid response protocols, and comprehensive incident documentation for critical facilities.',
      icon: AlertTriangle,
      image: 'https://images.unsplash.com/photo-1692085654366-5384c1df9af2',
    },
    {
      title: 'Comprehensive Safety Solutions',
      description: 'End-to-end fire safety consulting including code compliance, system design, installation oversight, and ongoing maintenance. Our holistic approach ensures regulatory compliance, system reliability, and optimal performance throughout the system lifecycle.',
      icon: Shield,
      image: 'https://images.pexels.com/photos/3208882/pexels-photo-3208882.jpeg',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1509228468518-180dd4864904')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        <div className="container relative z-10">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-16 flex items-center justify-center">
                <Flame className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Fire Fighting <span className="text-royal-gold">System</span>
                </h1>
              </div>
            </div>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Advanced fire detection, suppression, and emergency response solutions for critical infrastructure, government facilities, and high-security environments. Our comprehensive fire safety systems integrate cutting-edge technology with professional emergency response capabilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-16 flex items-center justify-center">
                      <section.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {section.title}
                      </h3>
                    </div>
                  </div>
                  <div className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {section.description}
                  </div>
                </div>
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img src={section.image} alt={section.title} className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-12 flex items-center justify-center">
                        <section.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FireFightingSystemPage; 